import { useEffect, useRef, useState } from "react";
import { appeler, ErreurApi } from "../api/client";
import { ApercuSchema } from "../api/schemas";
import type { Document } from "../api/schemas";

/**
 * §5.A — Aperçu INTÉGRÉ À LA PAGE (calque + iframe).
 *
 * Deux pièges payés en production, tous deux SILENCIEUX :
 *  · Safari bloque `window.open()` après un `await fetch` (hors geste utilisateur
 *    direct), sans aucun message. D'où : on n'ouvre jamais d'onglet, on affiche
 *    dans la page.
 *  · La CSP `default-src 'self'` sans `frame-src` refuse un aperçu `blob:` et
 *    laisse le cadre vide, sans message. D'où : URL R2 pré-signée, et `frame-src`
 *    déclaré explicitement côté serveur.
 *
 * Fermeture par Échap ou clic extérieur. Bascule automatique en téléchargement
 * pour les types non affichables.
 */
export function ApercuDocument({
  dossierId,
  document: doc,
  onFermer,
}: {
  dossierId: string;
  document: Document;
  onFermer: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [affichable, setAffichable] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const calque = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let annule = false;
    appeler(`/dossiers/${dossierId}/documents/${doc.id}/apercu`, ApercuSchema)
      .then((a) => {
        if (annule) return;
        setUrl(a.url);
        setAffichable(a.affichable);
      })
      .catch((e: ErreurApi) => !annule && setErreur(e.motif ?? e.message));
    return () => {
      annule = true;
    };
  }, [dossierId, doc.id]);

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => e.key === "Escape" && onFermer();
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [onFermer]);

  return (
    <div
      className="apercu"
      ref={calque}
      onMouseDown={(e) => e.target === calque.current && onFermer()}
      role="dialog"
      aria-modal="true"
      aria-label={`Aperçu de ${doc.nomFichier}`}
    >
      <div className="apercu__cadre">
        <header className="apercu__barre">
          <span className="apercu__nom">{doc.nomFichier}</span>
          {doc.scelle && <span className="jeton jeton--scelle">Scellé</span>}
          {doc.altere && <span className="jeton jeton--alerte">Altération détectée</span>}
          {/* Le lien de téléchargement est toujours présent : c'est la seule
              issue pour un type non affichable, et Safari n'y ajoute pas de
              condition puisque c'est un geste utilisateur direct. */}
          {url && (
            <a className="apercu__telecharger" href={url} download={doc.nomFichier}>
              Télécharger
            </a>
          )}
          <button type="button" onClick={onFermer} aria-label="Fermer l'aperçu">
            ×
          </button>
        </header>

        {erreur && <p className="apercu__erreur">{erreur}</p>}
        {!erreur && !url && <p className="apercu__attente">Ouverture…</p>}

        {url && affichable && (
          <iframe className="apercu__iframe" src={url} title={doc.nomFichier} />
        )}

        {url && !affichable && (
          <p className="apercu__non-affichable">
            Ce type de fichier ({doc.contentType}) n'est pas affichable dans le navigateur.
            Utilisez le bouton Télécharger.
          </p>
        )}
      </div>
    </div>
  );
}
